import React from 'react';
import { BgmTrack } from '../../data/bgmList';
import { useUserStore } from '../../stores/useUserStore';
import { useAudioStore } from '../../stores/useAudioStore';
import './BgmModal.css';

interface BgmModalProps {
  visible: boolean;
  lang?: 'ja' | 'en';
  bgmList: BgmTrack[];
  onClose: () => void;
  onPlay: (index: number) => void;
  onShowMessage?: (message: string, duration?: number) => void;
  onShowConfirm?: (message: string, onConfirm: () => void) => void;
}

const BgmModal: React.FC<BgmModalProps> = ({
  visible,
  lang = 'ja',
  bgmList,
  onClose,
  onPlay,
  onShowMessage,
  onShowConfirm,
}) => {
  const { donuts, spendDonut } = useUserStore();
  const { unlockBgm } = useAudioStore();

  if (!visible) return null;

  const handleTrackClick = (track: BgmTrack, originalIndex: number) => {
    if (track.unlocked) {
      onPlay(originalIndex);
      onClose();
    } else {
      if (donuts < track.price) {
        onShowMessage?.(
          lang === 'ja' ? 'ドーナツが足りないよ...' : 'Not enough donuts...'
        );
        return;
      }
      onShowConfirm?.(
        lang === 'ja'
          ? `${track.name}を${track.price}ドーナツで購入しますか？`
          : `Buy ${track.name} for ${track.price} donuts?`,
        () => {
          if (spendDonut(track.price)) {
            unlockBgm(track.id);
            onShowMessage?.(
              lang === 'ja'
                ? '新しいBGMが聴けるようになったよ！'
                : 'New BGM unlocked!'
            );
            setTimeout(() => onPlay(originalIndex), 100);
          }
        }
      );
    }
  };

  return (
    <div
      className="fixed inset-0 glass-overlay z-50 flex items-center justify-center p-4 fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-panel p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">
            {lang === 'ja' ? 'BGM選択' : 'BGM Selection'}
          </h2>
          <span className="text-sm font-bold px-3 py-1 rounded-full"
            style={{ color: '#ffd700', background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
            {donuts} donut{donuts !== 1 ? 's' : ''}
          </span>
        </div>

        {/* BGM list */}
        <div className="space-y-2 mb-4">
          {bgmList.map((track, originalIndex) => {
            const canAfford = donuts >= track.price;

            return (
              <div
                key={track.id}
                className={`border rounded-xl p-3 cursor-pointer transition-all ${
                  track.unlocked
                    ? 'bg-white/[0.04] border-white/[0.06] hover:bg-white/[0.08]'
                    : 'bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.06]'
                }`}
                onClick={() => handleTrackClick(track, originalIndex)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {!track.unlocked && (
                      <svg className="w-4 h-4 text-white/40 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="11" width="14" height="10" rx="2" fill="currentColor" opacity="0.6" />
                        <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    )}
                    <h3 className={`font-medium ${track.unlocked ? 'text-white/90' : 'text-white/50'}`}>
                      {track.name}
                    </h3>
                  </div>
                  <div className="text-right">
                    {!track.unlocked && (
                      <span className={`text-xs font-bold ${canAfford ? 'text-yellow-400' : 'text-red-400/70'}`}>
                        {track.price} donut{track.price !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={onClose}
          className="w-full py-2 px-4 glass-btn transition-colors"
        >
          {lang === 'ja' ? '閉じる' : 'Close'}
        </button>
      </div>
    </div>
  );
};

export default BgmModal;
